import { t } from "@lingui/core/macro";
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import {
  useIsClubSellingMemberships,
  useIsClubSellingStandardPackages,
} from "@/features/Club";
import {
  HomeIcon,
  LessonsIcon,
  ProfileIcon,
  EventsIcon,
  ClubSwitcherIcon,
  MembersIcon,
  RedirectIcon,
  StoreIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { IconProps } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../navigators/HomeTabsNavigator/types";
import { useFlags } from "../../../utils/launchDarkly";
import { showClubSwitcherModalAtom } from "../../Session";
import { useCurrentClubHasVisibleLessons } from "../../Session/hooks/useCurrentClubHasVisibleLessons";
import { navigationRef_atom } from "../atoms";
import { getEventNavigationLabel } from "../utils/eventNavigationLabel";
import {
  getNavigationForRoute,
  RouteLookupNavigation,
} from "../utils/getNavigationForRoute";
import { useGetTabBarItemsFragment_session$key } from "./__generated__/useGetTabBarItemsFragment_session.graphql";
import { useGoToStore } from "./useGoToStore";
import { useLessonTabHandler } from "./useLessonTabHandler";
import { useNavigationLabels } from "./useNavigationLabels";

export type TabBarItem = {
  routeHandler: () => void;
  label: string;
  icon: (props: IconProps) => React.JSX.Element;
  routeName?: string;
  selected: boolean;
  isDivider?: boolean;
};

type NavigationScope = "homeTabs" | "clubStack";

const LessonRouteNames = ["Lessons", "LessonsHome", "InstructorDetail"];
const ProfileRouteNames = ["ProfileHome", "ProfilePlayHistory", "Profile"];
const HomeRouteNames = ["Home", "MainHome"];
const EventsRouteNames = ["Events", "EventDetail"];

export const useGetTabBarItems = ({
  navigationScope = "homeTabs",
  openMemberModal,
  sessionRef,
  navigationOverride,
}: {
  navigationScope?: NavigationScope;
  openMemberModal: () => void;
  sessionRef: useGetTabBarItemsFragment_session$key;
  navigationOverride?: RouteLookupNavigation;
}) => {
  const navigationRef = useAtomValue(navigationRef_atom);
  const setShowClubSwitcherModal = useSetAtom(showClubSwitcherModalAtom);

  const contextNavigation = useNavigation<NavigationProp<ParamListBase>>();
  const navigation = navigationOverride ?? contextNavigation;

  const routeName = navigationRef?.isReady()
    ? navigationRef?.getCurrentRoute()?.name
    : null;

  const session = useFragment(useGetTabBarItemsFragmentString, sessionRef);

  const club = session?.currentClientProfile?.member?.club;
  const clubSlug = club?.slug;
  const goToStore = useGoToStore(session, navigationOverride, clubSlug);
  const lessonTabHandler = useLessonTabHandler(session, navigationOverride);

  const hasClubThatIsNotCurrent =
    session?.user?.activeMemberProfiles?.edges &&
    !session.user.activeMemberProfiles.edges.find(
      (e) => e?.node?.member?.club.slug === club?.slug
    );

  const canSwitchContext =
    (session?.user?.activeMemberProfiles?.count || 0) > 1 ||
    hasClubThatIsNotCurrent ||
    (Platform.OS === "web" && session?.staffAccessAvailable);

  const isClubSellingMemberships = useIsClubSellingMemberships(club);
  const isClubSellingStandardPackages = useIsClubSellingStandardPackages(club);

  const shouldShowStore =
    isClubSellingMemberships || isClubSellingStandardPackages;

  const hasEvents = !!club?.departments?.edges?.some(
    (e) => e?.node?.anyFacilityEventsEnabled
  );
  const hasMemberLessonBooking = useCurrentClubHasVisibleLessons(session);

  const isAnonymousUser = !session?.currentClientProfile?.id;

  const { memAppRosterVisibility } = useFlags();

  const showExternalLinks =
    (club?.appCustomization?.externalLinks?.length || 0) > 0;

  const externalLinks = club?.appCustomization?.externalLinks;

  const showMemberRoster = memAppRosterVisibility && club?.supportsPrivate;
  const lessonLabel = getNounOverride(
    club?.copyOverrides?.lesson?.plural,
    t`Lessons`
  );
  const eventLabel = getEventNavigationLabel(
    club?.copyOverrides?.event?.plural
  );
  const navigationLabels = useNavigationLabels();
  const items = useMemo(() => {
    const navigateToClubRoute = (screen: string, params?: object) => {
      if (
        navigationScope !== "clubStack" ||
        !clubSlug ||
        !navigationRef?.isReady()
      ) {
        return false;
      }

      navigationRef.navigate("Club", {
        clubSlug,
        screen,
        params,
      });

      return true;
    };

    const getClubNavigation = () =>
      navigationOverride?.getParent?.<
        NavigationProp<ClubStackNavigatorParamList>
      >() ??
      getNavigationForRoute<ClubStackNavigatorParamList>(navigation, "Home");

    const itemArray: TabBarItem[] = [
      {
        routeName: "MainHome",
        routeHandler: () => {
          if (navigationScope === "clubStack") {
            if (!navigateToClubRoute("Home", { screen: "MainHome" })) {
              getClubNavigation().navigate("Home", { screen: "MainHome" });
            }
          } else {
            getNavigationForRoute<HomeTabsNavigatorParamList>(
              navigation,
              "MainHome"
            ).navigate("MainHome");
          }
        },
        label: navigationLabels.home,
        icon: HomeIcon,
        selected: routeName ? HomeRouteNames.includes(routeName) : false,
      },
    ];

    if (hasMemberLessonBooking)
      itemArray.push({
        routeName: "Lessons",
        routeHandler: lessonTabHandler,
        label: lessonLabel,
        icon: LessonsIcon,
        selected: routeName ? LessonRouteNames.includes(routeName) : false,
      });

    if (hasEvents)
      itemArray.push({
        routeName: "Events",
        routeHandler: () => {
          if (navigationScope === "clubStack") {
            if (!navigateToClubRoute("Home", { screen: "Events" })) {
              getClubNavigation().navigate("Home", { screen: "Events" });
            }
          } else {
            getNavigationForRoute<HomeTabsNavigatorParamList>(
              navigation,
              "Events"
            ).navigate("Events");
          }
        },
        label: eventLabel,
        icon: EventsIcon,
        selected: routeName ? EventsRouteNames.includes(routeName) : false,
      });

    if (showMemberRoster)
      itemArray.push({
        routeName: "Members",
        routeHandler: () => {
          openMemberModal();
        },
        label: navigationLabels.members,
        icon: MembersIcon,
        selected: false,
      });

    if (shouldShowStore) {
      itemArray.push({
        routeHandler: () => {
          goToStore();
        },
        label: navigationLabels.store,
        icon: StoreIcon,
        selected: false,
      });
    }

    if (!isAnonymousUser)
      itemArray.push({
        routeName: "Profile",
        routeHandler: () => {
          if (navigationScope === "clubStack") {
            if (
              !navigateToClubRoute("Home", {
                screen: "Profile",
                params: { screen: "ProfileHome" },
              })
            ) {
              getClubNavigation().navigate("Home", {
                screen: "Profile",
                params: { screen: "ProfileHome" },
              });
            }
          } else {
            getNavigationForRoute<HomeTabsNavigatorParamList>(
              navigation,
              "Profile"
            ).navigate("Profile", { screen: "ProfileHome" });
          }
        },
        label: navigationLabels.profile,
        icon: ProfileIcon,
        selected: routeName ? ProfileRouteNames.includes(routeName) : false,
      });

    if (canSwitchContext) {
      itemArray.push({
        routeHandler: () => {
          setShowClubSwitcherModal(true);
        },
        label: navigationLabels.switchClub,
        icon: ClubSwitcherIcon,
        selected: false,
      });
    }

    if (showExternalLinks && externalLinks) {
      itemArray.push({
        routeHandler: () => {},
        label: "",
        icon: () => <></>,
        selected: false,
        isDivider: true,
      });
      externalLinks.forEach((link) => {
        if (link?.url && link?.displayName) {
          itemArray.push({
            routeHandler: () => {
              if (Platform.OS === "web") {
                window.open(link.url, "_blank");
              } else {
                if (navigationScope === "clubStack") {
                  if (
                    !navigateToClubRoute("Home", {
                      screen: "WebView",
                      params: {
                        url: link.url,
                        title: link.displayName,
                      },
                    })
                  ) {
                    getClubNavigation().navigate("Home", {
                      screen: "WebView",
                      params: {
                        url: link.url,
                        title: link.displayName,
                      },
                    });
                  }
                } else {
                  getNavigationForRoute<HomeTabsNavigatorParamList>(
                    navigation,
                    "WebView"
                  ).navigate("WebView", {
                    url: link.url,
                    title: link.displayName,
                  });
                }
              }
            },
            label: link.displayName,
            icon: ({ color }) => <RedirectIcon color={color} />,
            selected: false,
          });
        }
      });
    }

    return itemArray;
  }, [
    routeName,
    hasMemberLessonBooking,
    lessonTabHandler,
    lessonLabel,
    hasEvents,
    eventLabel,
    navigationLabels.home,
    navigationLabels.members,
    navigationLabels.profile,
    navigationLabels.store,
    navigationLabels.switchClub,
    showMemberRoster,
    shouldShowStore,
    isAnonymousUser,
    canSwitchContext,
    showExternalLinks,
    externalLinks,
    navigationScope,
    clubSlug,
    navigationRef,
    navigation,
    navigationOverride,
    openMemberModal,
    goToStore,
    setShowClubSwitcherModal,
  ]);

  const isSmallerThanXs = useBreakpointValue({
    base: true,
    xs: false,
    sm: false,
  });

  const isLgScreen = useBreakpointValue({
    base: false,
    md: false,
    lg: true,
  });

  const showOverflow =
    items.length > 4 || (isSmallerThanXs && items.length > 3);

  const itemsToSplice = isSmallerThanXs && !isLgScreen ? 2 : 3;

  // If we have more than 4 items to display, add to a new array for the overflow menu
  const overflowItems: TabBarItem[] = showOverflow
    ? [...[...items].splice(itemsToSplice)]
    : [];

  // Subtract the overflow items from items without creating a reference
  const remainingItems = items.filter((item) => !overflowItems.includes(item));

  return { items: remainingItems, overflowItems };
};

export const useGetTabBarItemsFragmentString = graphql`
  fragment useGetTabBarItemsFragment_session on Session {
    staffAccessAvailable
    ...useGoToStore_session
    ...useCurrentClubHasVisibleLessons_session
    ...useLessonTabHandler_session
    user {
      ... on MemberUser {
        activeMemberProfiles: memberProfiles(active: true, hidden: false) {
          count
          edges {
            node {
              hidden
              member {
                club {
                  slug
                }
              }
            }
          }
        }
      }
    }
    currentClientProfile {
      ... on MemberProfile {
        id
      }
      member {
        club {
          ...useIsClubSellingStandardPackages_club
          ...useIsClubSellingMemberships_club
          supportsPublic
          supportsPrivate
          slug
          copyOverrides {
            event {
              plural
            }
            lesson {
              plural
            }
          }
          appCustomization {
            externalLinks {
              displayName
              url
            }
          }
          departments {
            edges {
              node {
                anyFacilityEventsEnabled
                anyFacilityLessonsEnabled
              }
            }
          }
        }
      }
    }
  }
`;
